import React, { useEffect, useState } from 'react';
import './FlashMessage.css';

export interface FlashMessageProps {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
  onClose: (id: string) => void;
}

const FlashMessage: React.FC<FlashMessageProps> = ({
  id,
  type,
  message,
  duration = 5000,
  onClose
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setIsVisible(true), 100);
    
    // Auto-close after duration
    const closeTimer = setTimeout(() => {
      handleClose();
    }, duration);

    return () => {
      clearTimeout(timer);
      clearTimeout(closeTimer);
    };
  }, [duration]);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => {
      onClose(id);
    }, 300);
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return (
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'error':
        return (
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'warning':
        return (
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 9V13M12 17H12.01M10.29 3.86L1.82 18C1.64564 18.3024 1.57294 18.6453 1.61217 18.9873C1.6514 19.3292 1.80052 19.6547 2.03819 19.9101C2.27586 20.1655 2.59037 20.3379 2.933 20.4029C3.27564 20.4678 3.62967 20.4224 3.94 20.273L12.18 16.36C12.4063 16.2462 12.6737 16.2462 12.9 16.36L21.14 20.273C21.4503 20.4224 21.8044 20.4678 22.147 20.4029C22.4896 20.3379 22.8041 20.1655 23.0418 19.9101C23.2795 19.6547 23.4286 19.3292 23.4678 18.9873C23.5071 18.6453 23.4344 18.3024 23.26 18L14.79 3.86C14.6195 3.56631 14.3581 3.3329 14.0415 3.19315C13.7249 3.0534 13.3681 3.01376 13.025 3.07949C12.6819 3.14523 12.3701 3.31279 12.13 3.56L10.29 3.86Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'info':
        return (
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13 16H12V12H11M12 8H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div 
      className={`flash-message flash-message--${type} ${isVisible ? 'flash-message--visible' : ''} ${isLeaving ? 'flash-message--leaving' : ''}`}
      onClick={handleClose}
    >
      <div className="flash-message__content">
        <div className="flash-message__icon">
          {getIcon()}
        </div>
        <div className="flash-message__text">
          {message}
        </div>
        <button 
          className="flash-message__close"
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
          }}
          aria-label="Close message"
        >
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default FlashMessage;
